/* OS EDD - Top Shop Flavors (scoped, no globals) */
(() => {
  const SELECTOR_ROOT = '[data-os-edd-top-shop-flavors]';
  const SELECTOR_CTA = '[data-oseddflavors-cta]';
  const SELECTOR_STICKY = '[data-oseddflavors-sticky]';
  const SELECTOR_STICKY_CTA = '[data-oseddflavors-sticky-cta]';
  const CHECKOUT_REDIRECT_FLAG = '__oseddCheckoutRedirectUntil';

  function parseDiscountCodes(raw) {
    const text = (raw || '').trim();
    if (!text) return [];
    // Accept comma or newline separated
    return text
      .split(/[\n,]+/g)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function getSectionConfig(root) {
    return {
      bogoUrlParamsEnabled: root && root.dataset.bogoUrlParamsEnabled === 'true',
      bogoUrlParams: (root && root.dataset.bogoUrlParams) || '',
      checkoutUrl: (root && root.dataset.checkoutUrl) || '/checkout',
    };
  }

  function normalizeBogoParamString(rawValue) {
    if (!rawValue) return '';
    let normalized = String(rawValue).trim();
    if (!normalized) return '';

    if (normalized.indexOf('://') !== -1) {
      try {
        normalized = new URL(normalized).search.replace(/^\?/, '');
      } catch (err) {}
    }

    const queryStartIndex = normalized.indexOf('?');
    if (queryStartIndex > -1) {
      normalized = normalized.slice(queryStartIndex + 1);
    }

    normalized = normalized.replace(/^[?&]+/, '');

    const hashIndex = normalized.indexOf('#');
    if (hashIndex > -1) {
      normalized = normalized.slice(0, hashIndex);
    }

    return normalized.trim();
  }

  function applyBogoUrlParams(root, options) {
    const sectionConfig = getSectionConfig(root);
    const bogo = window.AmbrosiaBogoUrlParams;
    if (!bogo) return false;
    return bogo.apply(sectionConfig, options);
  }

  function extractBogoCartAttributes(root) {
    const sectionConfig = getSectionConfig(root);
    const raw = normalizeBogoParamString(sectionConfig.bogoUrlParams);
    if (!raw) return null;
    const attrs = {};
    let hasAny = false;
    new URLSearchParams(raw).forEach((value, key) => {
      if (!key) return;
      attrs[key] = value;
      hasAny = true;
    });
    return hasAny ? attrs : null;
  }

  function waitForBogoGift(knownItemCount) {
    return new Promise((resolve) => {
      let elapsed = 0;
      const interval = 200;
      const maxWait = 3000;

      function check() {
        fetch('/cart.js', { credentials: 'same-origin' })
          .then((r) => r.json())
          .then((cart) => {
            if (cart.item_count > knownItemCount) {
              resolve(true);
            } else {
              elapsed += interval;
              if (elapsed >= maxWait) {
                resolve(false);
              } else {
                setTimeout(check, interval);
              }
            }
          })
          .catch(() => resolve(false));
      }

      setTimeout(check, interval);
    });
  }

  function mergeConfiguredBogoParams(checkoutUrl, root) {
    const sectionConfig = getSectionConfig(root);
    if (!sectionConfig.bogoUrlParamsEnabled) return;

    const sanitizedParams = normalizeBogoParamString(sectionConfig.bogoUrlParams);
    if (!sanitizedParams) return;

    const incomingParams = new URLSearchParams(sanitizedParams);
    incomingParams.forEach((value, key) => {
      if (!key) return;
      checkoutUrl.searchParams.set(key, value);
    });
  }

  function getCheckoutUrlWithCurrentParams(root, discountCodes) {
    const sectionConfig = getSectionConfig(root);
    const checkoutBase = sectionConfig.checkoutUrl || '/checkout';
    const checkoutUrl = new URL(checkoutBase, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);

    currentParams.forEach((value, key) => {
      checkoutUrl.searchParams.set(key, value);
    });

    mergeConfiguredBogoParams(checkoutUrl, root);

    if (discountCodes && discountCodes.length > 0) {
      checkoutUrl.searchParams.set('discount', discountCodes.join(','));
    }

    if (checkoutUrl.origin === window.location.origin) {
      return checkoutUrl.pathname + checkoutUrl.search + checkoutUrl.hash;
    }
    return checkoutUrl.toString();
  }

  async function postJSON(url, bodyObj) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(bodyObj),
      credentials: 'same-origin',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  async function addItemsBatch(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: items || [] }),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  async function addSingleItem(item) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(item || {}),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  function wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function setStatus(root, msg) {
    const el = root.querySelector('[data-oseddflavors-status]');
    if (!el) return;
    el.textContent = msg || '';
  }

  function getScrollOffsetPx() {
    // If your theme header is visible/sticky, account for it.
    // If header is hidden (like your CSS), this will end up 0.
    try {
      const header = document.querySelector('.header-wrapper');
      if (!header) return 0;

      const style = window.getComputedStyle(header);
      if (style.display === 'none' || style.visibility === 'hidden') return 0;

      // Only offset if it’s fixed/sticky (common for Shopify headers)
      const pos = (style.position || '').toLowerCase();
      if (pos !== 'fixed' && pos !== 'sticky') return 0;

      const h = Math.round(header.getBoundingClientRect().height || 0);
      return Math.max(0, h);
    } catch (e) {
      return 0;
    }
  }

  function smoothScrollToTarget(targetEl) {
    if (!targetEl) return;

    const offset = getScrollOffsetPx();
    const rect = targetEl.getBoundingClientRect();
    const y = Math.round(window.pageYOffset + rect.top - offset - 12); // 12px breathing room

    window.scrollTo({
      top: Math.max(0, y),
      behavior: 'smooth',
    });
  }

  function bindInfoDropdowns(root) {
    const items = root.querySelectorAll('.osedd-topshop-flavors__infoItem');
    if (!items || !items.length) return;

    items.forEach((item) => {
      const btn = item.querySelector('[data-oseddflavors-info-btn]');
      const panel = item.querySelector('[data-oseddflavors-info-panel]');
      if (!btn || !panel) return;

      btn.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        // Close all siblings (accordion behavior)
        items.forEach((sib) => {
          if (sib === item) return;
          sib.classList.remove('is-open');
          const sibBtn = sib.querySelector('[data-oseddflavors-info-btn]');
          const sibPanel = sib.querySelector('[data-oseddflavors-info-panel]');
          if (sibBtn) sibBtn.setAttribute('aria-expanded', 'false');
          if (sibPanel) sibPanel.hidden = true;
        });

        if (isOpen) {
          item.classList.remove('is-open');
          btn.setAttribute('aria-expanded', 'false');
          panel.hidden = true;
        } else {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      });
    });
  }

  function bindFlavorSwitcher(root) {
    const select = root.querySelector('[data-oseddflavors-flavor-select]');

    if (select && !select.__oseddflavorsFlavorBound) {
      select.__oseddflavorsFlavorBound = true;

      select.addEventListener('change', () => {
        const nextUrl = (select.value || '').trim();
        if (!nextUrl) return;

        window.location.href = nextUrl;
      });
    }

    const radios = root.querySelectorAll('[data-oseddflavors-flavor-radio]');
    if (!radios || !radios.length) return;

    radios.forEach((radio) => {
      if (radio.__oseddflavorsFlavorRadioBound) return;
      radio.__oseddflavorsFlavorRadioBound = true;

      radio.addEventListener('keydown', (e) => {
        const key = e.key || '';

        if (key === ' ' || key === 'Spacebar') {
          e.preventDefault();
          radio.click();
        }

        if (key === 'Enter') {
          e.preventDefault();
          radio.click();
        }
      });
    });
  }

  function bindRteScrollLinks(root) {
    // Only handle anchor links inside this section’s RTE areas
    const rteContainers = root.querySelectorAll(
      '.osedd-topshop-flavors__awardBody.rte, .osedd-topshop-flavors__offerBody.rte, .osedd-topshop-flavors__awardBodyImgRight.rte'
    );
    if (!rteContainers || !rteContainers.length) return;

    rteContainers.forEach((wrap) => {
      wrap.addEventListener('click', (e) => {
        const a = e.target && e.target.closest ? e.target.closest('a') : null;
        if (!a) return;

        const href = (a.getAttribute('href') || '').trim();
        if (!href || href === '#') return;

        // Only intercept same-page hash links (e.g. #reviews)
        if (href.charAt(0) !== '#') return;

        const id = href.slice(1);
        if (!id) return;

        const target = document.getElementById(id);
        if (!target) return;

        e.preventDefault();
        smoothScrollToTarget(target);

        // Optional: keep URL hash in sync without jumping
        try {
          history.pushState(null, '', `#${id}`);
        } catch (err) {}
      });
    });
  }

  function setStickyVisible(root, isVisible) {
    if (!root) return;

    const sticky = root.querySelector(SELECTOR_STICKY);
    if (!sticky) return;

    if (isVisible) {
      root.classList.add('oseddflavors-sticky-active');
      sticky.setAttribute('aria-hidden', 'false');
    } else {
      root.classList.remove('oseddflavors-sticky-active');
      sticky.setAttribute('aria-hidden', 'true');
    }
  }

  function bindStickyObserver(root, mainCta) {
    if (!root || !mainCta) return;

    // Avoid double-binding observers
    if (root.__oseddflavorsStickyObserved) return;
    root.__oseddflavorsStickyObserved = true;

    const shouldShowSticky = () => {
      const r = mainCta.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const ctaHeight = Math.max(1, r.height || 1);

      const visiblePx = Math.min(r.bottom, viewportHeight) - Math.max(r.top, 0);
      const visibleRatio = Math.max(0, visiblePx) / ctaHeight;
      const isMainCtaVisibleEnough = visibleRatio >= 0.25;

      const isMobile = window.matchMedia
        ? window.matchMedia('(max-width: 899px)').matches
        : viewportHeight < 900;

      /*
        Desktop:
        - Keep original behavior: show sticky when static CTA is not sufficiently visible.

        Mobile:
        - Do NOT show sticky while user is still above the static CTA.
        - Only show sticky after the static CTA has been fully scrolled past.
      */
      if (isMobile) {
        return r.bottom <= 0;
      }

      return !isMainCtaVisibleEnough;
    };

    const updateStickyState = () => {
      setStickyVisible(root, shouldShowSticky());
    };

    window.addEventListener('scroll', updateStickyState, { passive: true });
    window.addEventListener('resize', updateStickyState); 

    // Fallback if IO not supported
    if (!('IntersectionObserver' in window)) {
      updateStickyState();
      return;
    }

    const obs = new IntersectionObserver(
      () => {
        updateStickyState();
      },
      {
        root: null,
        threshold: [0, 0.25, 0.5, 1],
      }
    );

    obs.observe(mainCta);
    root.__oseddflavorsStickyObs = obs;

    updateStickyState();
  }

  function setBusy(btn, isBusy) {
    if (!btn) return;
    btn.disabled = !!isBusy;
    btn.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    if (isBusy) btn.classList.add('is-busy');
    else btn.classList.remove('is-busy');
  }

  function setCheckoutRedirectGuard(durationMs = 6000) {
    window[CHECKOUT_REDIRECT_FLAG] = Date.now() + Math.max(1000, Number(durationMs) || 6000);
  }

  function clearCheckoutRedirectGuard() {
    delete window[CHECKOUT_REDIRECT_FLAG];
  }

  async function handleCheckoutClick(btn) {
    const root = btn.closest(SELECTOR_ROOT);
    if (!root) return;

    if (root.__oseddflavorsCheckoutSubmitting) return;
    root.__oseddflavorsCheckoutSubmitting = true;

    const mainCta = root.querySelector(SELECTOR_CTA);
    const stickyCta = root.querySelector(SELECTOR_STICKY_CTA);

    setStatus(root, '');
    setBusy(mainCta, true);
    setBusy(stickyCta, true);

    try {
      if (!window.AmbrosiaFastCheckout) {
        throw new Error('Checkout helper failed to load. Please refresh and try again.');
      }

      const clearCart = btn.getAttribute('data-oseddflavors-clear-cart') === '1';
      const mainVariantIdRaw = (btn.getAttribute('data-oseddflavors-main-variant') || '').trim();
      const mainQty = Number(btn.getAttribute('data-oseddflavors-main-qty') || 1);
      const mainSellingPlanRaw = (btn.getAttribute('data-oseddflavors-main-selling-plan') || '').trim();

      const mainVariantId = Number(mainVariantIdRaw || 0);
      const mainSellingPlanId = Number(mainSellingPlanRaw || 0);

      if (!mainVariantId) {
        throw new Error('Missing main variant ID. Please set it in the section settings.');
      }

      let freebies = [];
      const freebiesRaw = btn.getAttribute('data-oseddflavors-freebies') || '[]';

      try {
        freebies = JSON.parse(freebiesRaw) || [];
      } catch (e) {
        freebies = [];
      }

      const mainItem = {
        id: mainVariantId,
        quantity: Math.max(1, mainQty || 1),
      };

      if (mainSellingPlanRaw && mainSellingPlanId > 0) {
        mainItem.selling_plan = mainSellingPlanId;
      }

      const freebieItems = (freebies || [])
        .filter((it) => it && Number(it.id) > 0)
        .map((it) => ({
          id: Number(it.id),
          quantity: Math.max(1, Number(it.quantity) || 1),
        }));

      const sectionConfig = getSectionConfig(root);
      await window.AmbrosiaFastCheckout.addThenCheckout({
        sectionConfig: sectionConfig,
        clearCart: clearCart,
        items: [mainItem].concat(freebieItems),
        fallbackItems: [mainItem],
        waitForGift: sectionConfig.bogoUrlParamsEnabled,
        discountCodes: parseDiscountCodes(btn.getAttribute('data-oseddflavors-discounts')),
      });
      return;
    } catch (err) {
      const msg = err && err.message ? err.message : 'Unable to start checkout. Please try again.';
      setStatus(root, msg);
      setBusy(mainCta, false);
      setBusy(stickyCta, false);
      root.__oseddflavorsCheckoutSubmitting = false;
      if (window.AmbrosiaFastCheckout) window.AmbrosiaFastCheckout.endRedirecting();
    }
  }

  function bindRoot(root) {
    if (!root || root.__oseddflavorsBound) return;
    root.__oseddflavorsBound = true;

    if (applyBogoUrlParams(root, { allowReload: true })) return;

    const cta = root.querySelector(SELECTOR_CTA);
    if (cta) {
      cta.addEventListener('click', () => handleCheckoutClick(cta));
    }

    const stickyCta = root.querySelector(SELECTOR_STICKY_CTA);
    if (stickyCta) {
      stickyCta.addEventListener('click', () => handleCheckoutClick(stickyCta));
    }

    // Show sticky CTA only when main CTA scrolls out of view
    if (cta) {
      bindStickyObserver(root, cta);
    }

    // Dropdown info accordions (desktop + mobile placements)
    bindInfoDropdowns(root);

    // Flavor page switcher
    bindFlavorSwitcher(root);

    // Smooth-scroll for hash links inside RTE content (award/offer richtext)
    bindRteScrollLinks(root);

    // If primary flavor is sold out, redirect to fallback flavor URL
    const oseddFlavorSwitch = root.querySelector('.osedd-topshop-flavors__flavorSwitch');
    if (oseddFlavorSwitch) {
      const oseddRedirectUrl = (oseddFlavorSwitch.getAttribute('data-oseddflavors-redirect') || '').trim();
      if (oseddRedirectUrl) {
        const oseddCurrentPath = window.location.pathname + window.location.search;
        const oseddRedirectPath = oseddRedirectUrl.split('?')[0];
        if (oseddCurrentPath.indexOf(oseddRedirectPath) === -1) {
          window.location.replace(oseddRedirectUrl);
          return;
        }
      }
    }

    // Gallery: main image switch + thumb strip with side arrows
    const gallery = root.querySelector('[data-oseddflavors-gallery]');
    const mainSlides = root.querySelectorAll('[data-oseddflavors-gal-main-slide]');
    const thumbs = root.querySelectorAll('[data-oseddflavors-thumb]');
    const thumbViewport = root.querySelector('[data-oseddflavors-thumb-viewport]');
    const thumbList = root.querySelector('[data-oseddflavors-thumb-list]');
    const mainPrev = root.querySelector('[data-oseddflavors-main-nav="prev"]');
    const mainNext = root.querySelector('[data-oseddflavors-main-nav="next"]');

    // If thumbs exist, move the main-nav buttons down to sit beside the thumbs row (CSS handles positioning)
    if (root.querySelector('.osedd-topshop-flavors__galThumbs')) {
      root.classList.add('oseddflavors-has-thumbs');
    } else {
      root.classList.remove('oseddflavors-has-thumbs');
    }

    if (gallery && mainSlides.length && thumbs.length) {
      let index = 0;

      const syncMainNav = () => {
        if (!mainPrev || !mainNext) return;
        const max = Math.max(0, mainSlides.length - 1);
        mainPrev.disabled = index <= 0;
        mainNext.disabled = index >= max;
      };

      const setActive = (nextIndex) => {
        const max = Math.max(0, mainSlides.length - 1);
        index = Math.min(Math.max(nextIndex, 0), max);

        mainSlides.forEach((s, i) => {
          const isActive = i === index;
          s.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          if (isActive) s.setAttribute('data-oseddflavors-active', '1');
          else s.removeAttribute('data-oseddflavors-active');

          s.querySelectorAll('video').forEach((video) => {
            if (!isActive && typeof video.pause === 'function') video.pause();
          });
        });

        thumbs.forEach((t, i) => {
          const isActive = i === index;
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
          if (isActive) t.setAttribute('data-oseddflavors-active', '1');
          else t.removeAttribute('data-oseddflavors-active');
        });

        // Keep active thumb in view (NO centering -> prevents half-cut thumbs)
        const activeThumb = thumbs[index];
        if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
          activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }

        syncMainNav();
      };

      thumbs.forEach((t) => {
        t.addEventListener('click', () => {
          const i = Number(t.getAttribute('data-oseddflavors-gal-index') || 0);
          setActive(i);
        });
      });

      if (mainPrev) {
        mainPrev.addEventListener('click', (e) => {
          if (e && typeof e.preventDefault === 'function') e.preventDefault();
          setActive(index - 1);
        });
      }

      if (mainNext) {
        mainNext.addEventListener('click', (e) => {
          if (e && typeof e.preventDefault === 'function') e.preventDefault();
          setActive(index + 1);
        });
      }

      // Keep thumbs usable with native horizontal scrolling
      if (thumbViewport) {
        thumbViewport.style.scrollBehavior = 'smooth';
      }

      void thumbList;
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR_ROOT).forEach(bindRoot);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();